import { QueryFunctionContext, queryOptions } from "@tanstack/react-query";
import { USER_SERVICE_URL } from "../index";
import {
  Organisation,
  OrganisationInfo,
  User,
  Request,
  OrganisationDomain,
  Workspace,
  OtherUser,
  InvitedUser,
  Actions,
  Notification,
} from "./types";
import { authUrl, Client, refreshToken } from "../client.ts";
import { lsConstants } from "@/constants.ts";

export const checkMyUserInfo = async () => {
  try {
    await refreshToken({ redirectOnError: false })();
    const res = await fetch(USER_SERVICE_URL + "/me", {
      credentials: "include",
      headers: { "content-type": "application/json" },
    });

    if (!res.ok) return null;

    return await res.json();
  } catch {
    // Just checking if user is logged in.
    // If there is an error, we know user is logged out.
    // Need to return null as react query will error if it's undefined
    return null;
  }
};

const client = new Client(USER_SERVICE_URL, {
  before: [refreshToken()],
  credentials: "include",
  headers: { "content-type": "application/json" },
});

export const logout = async () => {
  await client.post("/logout", { before: [] });
  localStorage.removeItem(lsConstants.REDIRECT);
  localStorage.removeItem(lsConstants.TOKEN_EXPIRES);
  window.location.href = authUrl("/logout");
};

export const getMyUserInfo = () => {
  return client.get("/me") as Promise<User>;
};

export const listMyUserOrganisationsInfo = () => {
  return client.get("/me/organisations") as Promise<{
    data: OrganisationInfo[];
  }>;
};

export const listMyRequests = () => {
  return client.get("/me/requests") as Promise<{
    data: Request[];
  }>;
};

export const getOrganisationData = ({
  queryKey,
}: QueryFunctionContext<[string, string]>) => {
  return client.get("/organisation", {
    headers: { "Organisation-Id": queryKey[0] },
  }) as Promise<OrganisationInfo>;
};

type CreateOrg =
  | { name: string; createDefaultWorkspace?: never; joinCondition?: never }
  | {
      name: string;
      createDefaultWorkspace: true;
      joinCondition: "AUTO" | "INVITE";
    };

export const createOrganisation = <T extends CreateOrg>(json: T) => {
  return client.post("/organisation", { json }) as Promise<{
    organisation: { name: string; id: string };
    // Only returns workspace if flag passed to API
    workspace: T extends { createDefaultWorkspace: true }
      ? { name: string; id: string }
      : undefined;
  }>;
};

export const getOrganisationUsers =
  (includeInvites: boolean) =>
  ({ queryKey }: QueryFunctionContext<[string, string]>) => {
    return client.get(
      `/organisation/user?organisationId=${queryKey[0]}&includeInvites=${includeInvites}`
    ) as Promise<{
      data: (InvitedUser | OtherUser)[];
    }>;
  };

interface DeleteInvite {
  inviteId: string;
  email: string;
}

export const deleteInvites = (json: DeleteInvite) => {
  return client.delete("/invite", { json });
};

interface CreateWorkspace {
  name: string;
  organisationId: string;
}

export const createWorkspace = ({
  organisationId,
  ...json
}: CreateWorkspace) => {
  return client.post(`/workspace`, {
    headers: { "Organisation-Id": organisationId },
    json,
  }) as Promise<Workspace>;
};

interface InviteUser {
  emails: string[];
  organisationId: string;
  workspaceId: string;
  assessmentPackageName: string;
  assessmentPackageId: string;
  actions: string[];
  message: string | null;
}

export const shareTenderPackage = ({
  organisationId,
  workspaceId,
  ...json
}: InviteUser) => {
  return client.post(`/invite/assessment-package`, {
    headers: { "Organisation-Id": organisationId, "Workspace-Id": workspaceId },
    json,
  }) as Promise<{
    alreadyHasAccess: string[];
    existingUsers: string[];
    newUsers: string[];
  }>;
};

export interface UserWithPermissions extends User {
  actions: Actions[];
}

export const getUsersWithAccessToTenderPackage = ({
  queryKey,
}: QueryFunctionContext<[string, string, string, string]>) => {
  const [organisationId, workspaceId, assessmentPackageId] = queryKey;
  return client.get(`/invite/assessment-package/${assessmentPackageId}`, {
    headers: { "Organisation-Id": organisationId, "Workspace-Id": workspaceId },
  }) as Promise<{ data: (UserWithPermissions | InvitedUser)[] }>;
};

interface UpdateOrganisationInfo {
  organisationId: string;
  joinCondition?: string;
  name?: string;
  website?: string;
}

export const updateOrganisationInfo = ({
  organisationId,
  ...json
}: UpdateOrganisationInfo) => {
  return client.put("/organisation", {
    headers: { "Organisation-Id": organisationId },
    json,
  });
};

interface AddOrganisationDomain {
  organisationId: string;
  domain: string;
  verificationEmail?: string;
}

export const addOrganisationDomain = ({
  organisationId,
  ...json
}: AddOrganisationDomain) => {
  return client.post("/organisation/domain", {
    headers: { "Organisation-Id": organisationId },
    json,
  }) as Promise<OrganisationDomain>;
};

export const verifyDomain = ({
  queryKey,
}: QueryFunctionContext<[string, string]>) => {
  return client.post("/organisation/domain/verify", {
    before: [],
    json: { verificationId: queryKey[0] },
  }) as Promise<{ domain: string }>;
};

export const getOrganisationDomains = ({
  queryKey,
}: QueryFunctionContext<[string, string]>) => {
  return client.get("/organisation/domain", {
    headers: { "Organisation-Id": queryKey[0] },
  }) as Promise<{ data: OrganisationDomain[] }>;
};

export const getAvailableOrganisationsToJoin = () => {
  return client.get("/organisation/available") as Promise<{
    organisations: Organisation[];
  }>;
};

export const joinOrganisation = (organisationId: string) => {
  return client.post("/organisation/join", {
    json: { organisationId },
  }) as Promise<{ organisation: Organisation }>;
};

interface RequestToJoinWorkspace {
  workspaceId: string;
  organisationId: string;
}

export const requestToJoinWorkspace = ({
  organisationId,
  ...json
}: RequestToJoinWorkspace) => {
  return client.post("/workspace/join", {
    headers: { "Organisation-Id": organisationId },
    json,
  }) as Promise<Request>;
};

export const myWorkspaceJoinRequests = ({
  queryKey,
}: QueryFunctionContext<[string, string]>) => {
  return client.get("/workspace/join/me", {
    headers: { "Organisation-Id": queryKey[0] },
  }) as Promise<{ data: Request[] }>;
};

export const getAllWorkspaceRequests = ({
  queryKey,
}: QueryFunctionContext<string[]>) => {
  return client.get("/workspace/join", {
    headers: {
      "Organisation-Id": queryKey[0],
    },
  }) as Promise<{ data: Request[] }>;
};

interface DecideWorkspaceJoinRequest {
  organisationId: string;
  workspaceId: string;
  userId: string;
  isAccepted: boolean;
}

export const decideWorkspaceJoinRequest = ({
  organisationId,
  workspaceId,
  ...json
}: DecideWorkspaceJoinRequest) => {
  return client.post("/workspace/join/decide", {
    headers: {
      "Organisation-Id": organisationId,
      "Workspace-Id": workspaceId,
    },
    json,
  }) as Promise<{ data: Request[] }>;
};

interface RemoveUserFromWorkspace {
  organisationId: string;
  workspaceId: string;
  userId: string;
}

export const removeUserFromWorkspace = ({
  organisationId,
  workspaceId,
  ...json
}: RemoveUserFromWorkspace) => {
  return client.delete("/workspace/user", {
    headers: {
      "Organisation-Id": organisationId,
      "Workspace-Id": workspaceId,
    },
    json,
  }) as Promise<{ data: Request[] }>;
};

interface RemoveOrganisationDomain {
  organisationId: string;
  domain: string;
}

export const removeOrganisationDomain = ({
  organisationId,
  ...json
}: RemoveOrganisationDomain) => {
  return client.delete("/organisation/domain", {
    headers: { "Organisation-Id": organisationId },
    json,
  });
};

export const getUserData = ({
  queryKey,
}: QueryFunctionContext<[string, string, string]>) => {
  return client.get(`/user/${queryKey[1]}`, {
    headers: { "Organisation-Id": queryKey[0] },
  }) as Promise<OtherUser>;
};

interface RemoveUserFromOrganisation {
  userId: string;
  organisationId: string;
}

export const removeUserFromOrganisation = async ({
  userId,
  organisationId,
}: RemoveUserFromOrganisation) => {
  return client.delete("/organisation/user", {
    headers: { "Organisation-Id": organisationId },
    json: { userId },
  });
};

interface AddMemberToWorkspace {
  userId: string;
  organisationId: string;
  workspaceId: string;
  role: "WORKSPACE_ADMIN" | "MEMBER";
}

export const addMemberToWorkspace = async ({
  userId,
  organisationId,
  workspaceId,
  role,
}: AddMemberToWorkspace) => {
  return client.post("/workspace/add", {
    headers: {
      "Organisation-Id": organisationId,
      "Workspace-Id": workspaceId,
    },
    json: { userId, userType: role },
  });
};

interface InviteUserToWorkspace {
  organisationId: string;
  workspaceIds: string[];
  email: string;
}

export const inviteUserToWorkspace = async ({
  organisationId,
  workspaceIds,
  email,
}: InviteUserToWorkspace) => {
  return client.post("/workspace/invite", {
    headers: {
      "Organisation-Id": organisationId,
    },
    json: { email, workspaceIds },
  });
};

interface PromoteToAdmin {
  organisationId: string;
  userId: string;
}

export const promoteToAdmin = async ({
  organisationId,
  userId,
}: PromoteToAdmin) => {
  return client.post("/organisation/admin", {
    headers: { "Organisation-Id": organisationId },
    json: { userId },
  });
};

interface RemoveAdmin {
  organisationId: string;
  userId: string;
}

export const removeAdmin = async ({ organisationId, userId }: RemoveAdmin) => {
  return client.delete("/organisation/admin", {
    headers: { "Organisation-Id": organisationId },
    json: { userId },
  });
};

export const updateUserName = async (name: string) => {
  return client.put("/user/name", { json: { name } });
};

interface UpdateWorkspace {
  organisationId: string;
  workspaceId: string;
  name: string;
}

export const updateWorkspace = async ({
  organisationId,
  workspaceId,
  name,
}: UpdateWorkspace) => {
  return client.put("/workspace", {
    headers: { "Organisation-Id": organisationId, "Workspace-Id": workspaceId },
    json: { name },
  });
};

interface PromoteToWorkspaceAdmin {
  organisationId: string;
  workspaceId: string;
  userId: string;
}

export const promoteToWorkspaceAdmin = async ({
  organisationId,
  workspaceId,
  userId,
}: PromoteToWorkspaceAdmin) => {
  return client.post("/workspace/admin", {
    headers: { "Organisation-Id": organisationId, "Workspace-Id": workspaceId },
    json: { userId },
  });
};

interface RemoveWorkspaceAdmin {
  organisationId: string;
  workspaceId: string;
  userId: string;
}

export const removeWorkspaceAdmin = async ({
  organisationId,
  workspaceId,
  userId,
}: RemoveWorkspaceAdmin) => {
  return client.delete("/workspace/admin", {
    headers: { "Organisation-Id": organisationId, "Workspace-Id": workspaceId },
    json: { userId },
  });
};

export const getMyInvites = async () => {
  return client.get("/invite") as Promise<{
    data: InvitedUser[];
  }>;
};

interface HandleMyOrganisationInvite {
  inviteId: string;
  join: boolean;
}

export const handleMyOrganisationInvite = async ({
  inviteId,
  join,
}: HandleMyOrganisationInvite) => {
  return client.post(`/invite/${inviteId}`, { json: { join } }) as Promise<{
    data: InvitedUser[];
  }>;
};

interface UpdateUserPermissions {
  userId: string;
  organisationId: string;
  workspaceId: string;
  resourceType: string;
  resourceId: string;
  actions: string[];
}

export const updateUserPermissions = async ({
  userId,
  organisationId,
  workspaceId,
  resourceType,
  resourceId,
  actions,
}: UpdateUserPermissions) => {
  return client.post(`/permission`, {
    headers: { "Organisation-Id": organisationId, "Workspace-Id": workspaceId },
    json: { userId, resourceType, resourceId, actions },
  });
};

export const listMyNotifications = async ({
  queryKey,
}: QueryFunctionContext<[string, string | undefined, string]>) => {
  const [organisationId, workspaceId] = queryKey;
  return client.get("/me/notification", {
    headers: {
      "Organisation-Id": organisationId,
      "Workspace-Id": workspaceId || "",
    },
  }) as Promise<{ data: Notification[] }>;
};

export const readMyNotifications = async ({
  eventIds,
}: {
  eventIds: string[];
}) => {
  return client.post("/me/notification/read", {
    body: JSON.stringify({
      eventIds,
    }),
  }) as Promise<{ data: Notification[] }>;
};

export const listWorkspaceUsersQuery = (
  organisationId: string,
  workspaceId: string,
  includeInvites: boolean
) => {
  return queryOptions({
    queryKey: [
      organisationId,
      "workspace",
      workspaceId,
      "users",
      includeInvites,
    ],
    queryFn: () =>
      client.get(`/workspace/user?includeInvites=${includeInvites}`, {
        headers: {
          "Organisation-Id": organisationId,
          "Workspace-Id": workspaceId,
        },
      }) as Promise<{
        data: (InvitedUser | User)[];
      }>,
  });
};

export const toggleWorkspaceFeatures = (params: {
  createAssessment?: boolean;
  organisationId: string;
  workspaceId: string;
}) => {
  const { organisationId, workspaceId, ...features } = params;
  return client.put(`/workspace/features`, {
    headers: {
      "Organisation-Id": organisationId,
      "Workspace-Id": workspaceId,
    },
    json: features,
  });
};
